/**
 * Generic Column Settings Modal
 * Allows users to configure which columns are visible and their order
 */

import { useState, useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AppsIcon from '@mui/icons-material/Apps'; // Using Apps icon as it looks like the drag handle (grid dots)
import LockIcon from '@mui/icons-material/Lock';
import { ColumnConfig } from './ColumnVisibilityContext';

interface ColumnSettingsModalProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onApply: (updatedColumns: ColumnConfig[]) => void;
  onReset?: () => ColumnConfig[]; // Optional reset callback that returns default columns
  title?: string;
}

export const ColumnSettingsModal = ({ open, onClose, columns, onApply, onReset, title = 'Adjust Displayed Columns' }: ColumnSettingsModalProps) => {
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>(columns);
  const dragItem = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  if (!open) return null;

  // Get visible columns in order
  const visibleColumns = localColumns.filter((col) => col.isVisible);

  // Get available columns (not visible)
  const availableColumns = localColumns.filter((col) => !col.isVisible);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    dragItem.current = position;
    // Set visual effect
    e.currentTarget.style.opacity = '0.5';
    // Clear drag image ghost if needed, or let default browser behavior handle it
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    // Only update if different to avoid excessive renders
    if (dragOverIndex !== position) {
      setDragOverIndex(position);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Optional: could verify if leaving the container to clear, but often simpler to just rely on new Enter
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';

    const dragIndex = dragItem.current;

    // Reset state
    setDragOverIndex(null);
    dragItem.current = null;

    if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) {
      return;
    }

    const newVisibleColumns = [...visibleColumns];
    const draggedItemContent = newVisibleColumns[dragIndex];
    newVisibleColumns.splice(dragIndex, 1);
    newVisibleColumns.splice(dragOverIndex, 0, draggedItemContent);

    // Merge back with hidden columns
    const newColumns = [...newVisibleColumns, ...availableColumns];
    setLocalColumns(newColumns);
  };

  const handleAddColumn = (columnId: string) => {
    setLocalColumns((prev) => {
      const columnToAdd = prev.find((c) => c.id === columnId);
      if (!columnToAdd) return prev;

      // Split into visible and hidden, excluding the target column
      const visibleCols = prev.filter((c) => c.isVisible && c.id !== columnId);
      const hiddenCols = prev.filter((c) => !c.isVisible && c.id !== columnId);

      // Append the new column to the end of the visible list
      const newColumn = { ...columnToAdd, isVisible: true };

      return [...visibleCols, newColumn, ...hiddenCols];
    });
  };

  const handleRemoveColumn = (columnId: string) => {
    const column = localColumns.find((col) => col.id === columnId);
    if (column?.isLocked) return; // Cannot remove locked columns

    setLocalColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, isVisible: false } : col)));
  };

  const handleReset = () => {
    if (onReset) {
      // Get default columns from reset callback and update local state only
      const defaultColumns = onReset();
      setLocalColumns(defaultColumns);
    } else {
      // Default reset: all columns visible
      setLocalColumns((prev) => prev.map((col) => ({ ...col, isVisible: true })));
    }
  };

  const handleApply = () => {
    onApply(localColumns);
    onClose();
  };

  return (
    <div className='fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50 font-sans'>
      <div className='bg-white rounded-12 shadow-xl w-full max-w-[800px] mx-16 max-h-[90vh] flex flex-col overflow-hidden'>
        {/* Header */}
        <div className='flex items-center justify-between px-24 py-20 border-b border-neutral-20'>
          <h2 className='text-18 font-bold text-neutral-100'>{title}</h2>
          <div className='flex items-center gap-16'>
            <button type='button' onClick={handleReset} className='text-14 text-neutral-60 hover:text-neutral-80 font-medium transition-colors'>
              Reset
            </button>
            <button type='button' onClick={onClose} className='text-neutral-60 hover:text-neutral-80 transition-colors' aria-label='Close column settings'>
              <CloseIcon sx={{ fontSize: 24 }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-24 py-24'>
          {/* ORDER Section */}
          <div className='mb-32'>
            <h3 className='text-12 font-bold text-neutral-40 uppercase mb-12 tracking-wide'>ORDER</h3>
            <div className='bg-[#F8F9FA] rounded-12 p-16 min-h-[100px] border border-transparent'>
              <div className='flex flex-wrap gap-8'>
                {visibleColumns.map((col, index) => (
                  <div
                    key={col.id}
                    draggable={!col.isLocked}
                    onDragStart={(e) => !col.isLocked && handleDragStart(e, index)}
                    onDragEnter={(e) => !col.isLocked && handleDragEnter(e, index)}
                    onDragEnd={!col.isLocked ? handleDragEnd : undefined}
                    onDragOver={(e) => !col.isLocked && e.preventDefault()}
                    className={`relative inline-flex items-center h-32 bg-white border border-neutral-20 rounded-full pl-12 pr-8 gap-8 shadow-sm transition-colors ${
                      col.isLocked ? 'cursor-default opacity-60' : 'cursor-move hover:border-neutral-40'
                    }`}
                    aria-label={col.isLocked ? `${col.label} (locked)` : `Drag to reorder ${col.label}`}
                  >
                    {/* Drop Indicator - only show for non-locked columns */}
                    {!col.isLocked && index === dragOverIndex && <div className='absolute -left-6 top-4 bottom-4 w-4 bg-[#00A651] rounded-full z-10' />}

                    <span className='text-12 font-bold text-neutral-40 min-w-[16px]'>{index + 1}</span>
                    <span className='text-14 font-medium text-neutral-100'>{col.label}</span>
                    <div className={`flex items-center gap-4 ml-4 ${col.isLocked ? 'text-neutral-40' : 'text-neutral-40'}`}>
                      {/* Show lock icon for locked columns, drag handle for others */}
                      {col.isLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <AppsIcon sx={{ fontSize: 16 }} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ITEMS Section */}
          <div>
            <h3 className='text-12 font-bold text-neutral-40 uppercase mb-12 tracking-wide'>ITEMS</h3>
            <div className='flex flex-wrap gap-10'>
              {/* Sort all columns alphabetically for ITEMS section */}
              {[...localColumns]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((col) => {
                  const { isVisible } = col;

                  if (isVisible) {
                    // Show visible columns with remove button (green)
                    return (
                      <button
                        key={col.id}
                        type='button'
                        onClick={() => !col.isLocked && handleRemoveColumn(col.id)}
                        disabled={col.isLocked}
                        aria-label={col.isLocked ? `${col.label} (locked, always visible)` : `Remove ${col.label} from visible columns`}
                        className={`inline-flex items-center h-32 rounded-full px-12 gap-8 text-14 font-medium transition-colors border ${
                          col.isLocked
                            ? 'bg-[#E6F6EC] text-[#00A651] border-[#00A651] cursor-not-allowed opacity-80'
                            : 'bg-[#E6F6EC] text-[#00A651] border-[#00A651] hover:bg-[#D1F0DB]'
                        }`}
                      >
                        <span>{col.label}</span>
                        {!col.isLocked && (
                          <span className='flex items-center justify-center w-14 h-14 bg-[#00A651] text-white rounded-full' aria-hidden='true'>
                            <CloseIcon sx={{ fontSize: 10 }} />
                          </span>
                        )}
                      </button>
                    );
                  }

                  // Show available columns (gray)
                  return (
                    <button
                      key={col.id}
                      type='button'
                      onClick={() => handleAddColumn(col.id)}
                      aria-label={`Add ${col.label} to visible columns`}
                      className='inline-flex items-center h-32 bg-white border border-neutral-30 text-neutral-100 rounded-full px-16 text-14 font-medium hover:bg-neutral-5 hover:border-neutral-40 transition-all'
                    >
                      <span>{col.label}</span>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='px-24 py-16 border-t border-neutral-20 bg-white'>
          <button
            type='button'
            onClick={handleApply}
            className='w-full bg-[#00A651] hover:bg-[#008f45] text-white font-bold h-48 rounded-8 transition-colors text-16 shadow-sm'
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
